import { Suspense, useEffect } from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';

import Layout from './components/layout/Layout';
import HomePage from './pages/HomePage';
import AdminRouteGuard from './components/admin/AdminRouteGuard';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { ChunkErrorBoundary } from './components/error';
import { 
  ErrorBoundaryConfigProvider, 
  ConfiguredRouteErrorBoundary,
  ConfiguredErrorBoundary 
} from './components/error/ErrorBoundaryConfig';
import { lazyWithRetry } from './utils/lazyWithRetry';
import { requireValidEnvironment, getValidationReport } from './lib/envValidation';
import EnvironmentValidator from './components/dev/EnvironmentValidator';
import PerformanceDashboard from './components/dev/PerformanceDashboard';

// Enhanced lazy-loaded pages with retry mechanism
const PropertyListingPage = lazyWithRetry(() => import('./pages/PropertyListingPage'));
const PropertyDetailPage = lazyWithRetry(() => import('./pages/PropertyDetailPage'));
const Dashboard = lazyWithRetry(() => import('./pages/Dashboard'));
const SignInPage = lazyWithRetry(() => import('./pages/SignInPage'));
const RegisterEnhancedPage = lazyWithRetry(() => import('./pages/RegisterEnhancedPage'));
const SearchPage = lazyWithRetry(() => import('./pages/SearchPage'));
const NotFoundPage = lazyWithRetry(() => import('./pages/NotFoundPage'));
const ForgotPasswordPage = lazyWithRetry(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazyWithRetry(() => import('./pages/ResetPasswordPage'));
const CareersPage = lazyWithRetry(() => import('./pages/CareersPage'));
const TermsPage = lazyWithRetry(() => import('./pages/TermsPage'));
const PrivacyPage = lazyWithRetry(() => import('./pages/PrivacyPage'));
const AboutPage = lazyWithRetry(() => import('./pages/AboutPage'));
const ContactPage = lazyWithRetry(() => import('./pages/ContactPage'));
const HelpPage = lazyWithRetry(() => import('./pages/HelpPage'));
const FacebookDataDeletionPage = lazyWithRetry(() => import('./pages/FacebookDataDeletionPage'));
const TestAuthPage = lazyWithRetry(() => import('./pages/TestAuthPage'));
const UnauthorizedPage = lazyWithRetry(() => import('./pages/UnauthorizedPage'));
const AgentSearchPage = lazyWithRetry(() => import('./pages/AgentSearchPage'));
const PublicAgentProfile = lazyWithRetry(() => import('./pages/PublicAgentProfile'));
const BlogPage = lazyWithRetry(() => import('./pages/BlogPage'));
const BlogPostPage = lazyWithRetry(() => import('./pages/BlogPostPage'));
const AdminLoginPage = lazyWithRetry(() => import('./pages/admin/AdminLoginPage'));
const AdminLayout = lazyWithRetry(() => import('./components/admin/AdminLayout'));
const AdminDashboard = lazyWithRetry(() => import('./pages/admin/AdminDashboard'));

// Loading fallback — CSS-only so the entry graph does not depend on lucide-react here.
const PageLoading = () => (
  <div className="flex h-screen w-full items-center justify-center" role="status" aria-label="Loading">
    <span
      className="h-10 w-10 animate-spin rounded-full border-2 border-primary-200 border-t-primary-600"
      aria-hidden
    />
  </div>
);

// Error boundary configuration
const errorBoundaryConfig = {
  enableGlobalErrorReporting: true,
  showErrorDetails: process.env.NODE_ENV === 'development',
  showRetryButtons: true,
  showHomeButtons: true,
  formErrorBoundary: {
    showResetButton: true,
    showSaveButton: false,
  },
  dataErrorBoundary: {
    showRefreshButton: true,
    showDownloadButton: false,
  },
  routeErrorBoundary: {
    showBackButton: true,
  },
  onGlobalError: (error: Error, errorInfo: React.ErrorInfo) => {
    console.error('Global error caught:', error, errorInfo);
    // You can integrate with your error reporting service here
  },
  onFormError: (error: Error, errorInfo: React.ErrorInfo) => {
    console.error('Form error caught:', error, errorInfo);
    // You can integrate with your form error tracking here
  },
  onDataError: (error: Error, errorInfo: React.ErrorInfo) => {
    console.error('Data error caught:', error, errorInfo);
    // You can integrate with your data error tracking here
  },
  onRouteError: (error: Error, errorInfo: React.ErrorInfo) => {
    console.error('Route error caught:', error, errorInfo);
    // You can integrate with your route error tracking here
  },
};

function App() {
  useEffect(() => {
    try {
      requireValidEnvironment();
      console.log('✅ Environment validation passed');
    } catch (error) {
      console.error('🚨 Environment validation failed:', error);
      console.error(getValidationReport());
      
      // Only alert in development mode, in production show a user-friendly banner
      if (import.meta.env.DEV) {
        alert('Application cannot start due to environment validation errors. Please check console for details.');
      }
    }

    // Add global error handler for runtime errors
    const handleGlobalError = (event: ErrorEvent) => {
      console.error('🚨 Global error caught:', event.error);
      
      // Show user-friendly error message
      const errorDiv = document.createElement('div');
      errorDiv.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        background: #dc2626;
        color: white;
        padding: 1rem;
        text-align: center;
        z-index: 9999;
        font-family: system-ui, sans-serif;
        font-size: 14px;
      `;
      errorDiv.innerHTML = `
        🚨 Something went wrong. Please refresh the page or contact support if this persists.
        <br><small>Error: ${event.error?.message || 'Unknown error'}</small>
      `;
      document.body.appendChild(errorDiv);
      
      // Remove after 15 seconds
      setTimeout(() => {
        if (errorDiv.parentNode) {
          errorDiv.parentNode.removeChild(errorDiv);
        }
      }, 15000);
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('🚨 Unhandled promise rejection:', event.reason);
      
      // Show user-friendly error message
      const errorDiv = document.createElement('div');
      errorDiv.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        background: #dc2626;
        color: white;
        padding: 1rem;
        text-align: center;
        z-index: 9999;
        font-family: system-ui, sans-serif;
        font-size: 14px;
      `;
      errorDiv.innerHTML = `
        🚨 Network or data error occurred. Please refresh the page or try again later.
        <br><small>Error: ${event.reason?.message || 'Unknown error'}</small>
      `;
      document.body.appendChild(errorDiv);
      
      // Remove after 15 seconds
      setTimeout(() => {
        if (errorDiv.parentNode) {
          errorDiv.parentNode.removeChild(errorDiv);
        }
      }, 15000);
    };

    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  return (
    <ErrorBoundaryConfigProvider config={errorBoundaryConfig}>
      <ChunkErrorBoundary>
        <Suspense fallback={<PageLoading />}>
          <Routes>
            <Route path="/" element={<Layout />}>
              <Route index element={
                <ConfiguredErrorBoundary componentName="HomePage">
                  <HomePage />
                </ConfiguredErrorBoundary>
              } />
              <Route path="properties" element={
                <ConfiguredRouteErrorBoundary routeName="Property Listing">
                  <PropertyListingPage />
                </ConfiguredRouteErrorBoundary>
              } />
              <Route path="properties/:id" element={
                <ConfiguredRouteErrorBoundary routeName="Property Detail">
                  <PropertyDetailPage />
                </ConfiguredRouteErrorBoundary>
              } />
              <Route path="search" element={
                <ConfiguredRouteErrorBoundary routeName="Search">
                  <SearchPage />
                </ConfiguredRouteErrorBoundary>
              } />
              <Route path="find-agents" element={<Navigate to="/agents" replace />} />
              <Route path="agents" element={
                <ConfiguredRouteErrorBoundary routeName="Agent Search">
                  <AgentSearchPage />
                </ConfiguredRouteErrorBoundary>
              } />
              <Route path="agents/:agentId" element={
                <ConfiguredRouteErrorBoundary routeName="Agent Profile">
                  <PublicAgentProfile />
                </ConfiguredRouteErrorBoundary>
              } />
              <Route path="blog" element={
                <ConfiguredRouteErrorBoundary routeName="Blog">
                  <BlogPage />
                </ConfiguredRouteErrorBoundary>
              } />
              <Route path="blog/:slug" element={
                <ConfiguredRouteErrorBoundary routeName="Blog Post">
                  <BlogPostPage />
                </ConfiguredRouteErrorBoundary>
              } />
              <Route path="about" element={
                <ConfiguredRouteErrorBoundary routeName="About">
                  <AboutPage />
                </ConfiguredRouteErrorBoundary>
              } />
              <Route path="careers" element={
                <ConfiguredRouteErrorBoundary routeName="Careers">
                  <CareersPage />
                </ConfiguredRouteErrorBoundary>
              } />
              <Route path="help" element={
                <ConfiguredRouteErrorBoundary routeName="Help">
                  <HelpPage />
                </ConfiguredRouteErrorBoundary>
              } />
              <Route path="terms" element={
                <ConfiguredRouteErrorBoundary routeName="Terms">
                  <TermsPage />
                </ConfiguredRouteErrorBoundary>
              } />
              <Route path="privacy" element={
                <ConfiguredRouteErrorBoundary routeName="Privacy">
                  <PrivacyPage />
                </ConfiguredRouteErrorBoundary>
              } />
              <Route path="facebook-data-deletion" element={
                <ConfiguredRouteErrorBoundary routeName="Facebook Data Deletion">
                  <FacebookDataDeletionPage />
                </ConfiguredRouteErrorBoundary>
              } />
              <Route path="contact" element={
                <ConfiguredRouteErrorBoundary routeName="Contact">
                  <ContactPage />
                </ConfiguredRouteErrorBoundary>
              } />
              <Route path="how-it-works" element={<Navigate to="/#how-it-works" replace />} />
              <Route path="signin" element={
                <ConfiguredRouteErrorBoundary routeName="Sign In">
                  <SignInPage />
                </ConfiguredRouteErrorBoundary>
              } />
              <Route path="forgot-password" element={
                <ConfiguredRouteErrorBoundary routeName="Forgot Password">
                  <ForgotPasswordPage />
                </ConfiguredRouteErrorBoundary>
              } />
              <Route path="reset-password" element={
                <ConfiguredRouteErrorBoundary routeName="Reset Password">
                  <ResetPasswordPage />
                </ConfiguredRouteErrorBoundary>
              } />
              <Route path="register-enhanced" element={
                <ConfiguredRouteErrorBoundary routeName="Register">
                  <RegisterEnhancedPage />
                </ConfiguredRouteErrorBoundary>
              } />
              <Route path="unauthorized" element={
                <ConfiguredRouteErrorBoundary routeName="Unauthorized">
                  <UnauthorizedPage />
                </ConfiguredRouteErrorBoundary>
              } />
              <Route path="dashboard/*" element={
                <ProtectedRoute>
                  <ConfiguredRouteErrorBoundary routeName="Dashboard">
                    <Dashboard />
                  </ConfiguredRouteErrorBoundary>
                </ProtectedRoute>
              } />
              <Route path="test-auth" element={
                <ConfiguredRouteErrorBoundary routeName="Test Auth">
                  <TestAuthPage />
                </ConfiguredRouteErrorBoundary>
              } />
              <Route path="*" element={
                <ConfiguredRouteErrorBoundary routeName="Not Found">
                  <NotFoundPage />
                </ConfiguredRouteErrorBoundary>
              } />
            </Route>
            
            {/* Admin Routes */}
            <Route path="/admin/login" element={
              <ConfiguredRouteErrorBoundary routeName="Admin Login">
                <AdminLoginPage />
              </ConfiguredRouteErrorBoundary>
            } />
            <Route path="/admin/dashboard" element={
              <AdminLayout>
                <AdminRouteGuard>
                  <ConfiguredRouteErrorBoundary routeName="Admin Dashboard">
                    <AdminDashboard />
                  </ConfiguredRouteErrorBoundary>
                </AdminRouteGuard>
              </AdminLayout>
            } />
          </Routes>
        </Suspense>
        
        {/* Development Environment Validator */}
        <EnvironmentValidator />
        
        {/* Performance Dashboard (Development Only) */}
        {process.env.NODE_ENV === 'development' && <PerformanceDashboard />}
      </ChunkErrorBoundary>
    </ErrorBoundaryConfigProvider>
  );
}

export default App;